// src/components/PurchaseModal.tsx
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle
} from './ui/dialog';
import { Separator }     from './ui/separator';
import { Button }        from './ui/button';
import {
  Shield, FileText, Check,
  CreditCard, MessageCircle
} from 'lucide-react';
import { FaPaypal } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import unknownLogo       from '../assets/unknown_logo.png';

import {
  createCheckoutSession,
  createPaypalOrder,
} from '../api/paymentService';
import { PaypalCreateResponse } from '../types/interfaces';

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: string;
  logo?: string;
  price: number;               // Precio SIN descuento
  finalPrice: number;          // Precio CON descuento
  discountPercentage: number;  // % de descuento
  discountEndDate?: string;    // ISO timestamp
  currencySymbol: string;
  findings: Record<string, number>;
  domains: { domain_name: string }[];
  urls:    { url: string }[];
  reportId: string;
  currency: string;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  isOpen, onClose, company, logo,
  price, finalPrice, discountPercentage, discountEndDate,
  findings, domains, urls, reportId, currencySymbol, currency
}) => {
  const { t, i18n } = useTranslation();
  const [remainingTime, setRemainingTime] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'stripe'|'paypal'>('stripe');

  const isDiscountValid =
    discountPercentage > 0 &&
    !!discountEndDate &&
    new Date(discountEndDate).getTime() > Date.now();

  // Cuenta atrás para el descuento
  useEffect(() => {
    if (!isDiscountValid) return;
    const iv = setInterval(() => {
      const diff = new Date(discountEndDate!).getTime() - Date.now();
      if (diff > 0) {
        const d = Math.floor(diff/86400000);
        const h = Math.floor((diff%86400000)/3600000);
        const m = Math.floor((diff%3600000)/60000);
        const s = Math.floor((diff%60000)/1000);
        setRemainingTime(`${d}d ${h}h ${m}m ${s}s`);
      } else {
        clearInterval(iv);
        setRemainingTime('');
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [discountEndDate, isDiscountValid]);

  const handlePurchase = async () => {
    try {
      let redirectTo: string;
      if (paymentMethod === 'stripe') {
        redirectTo = await createCheckoutSession(reportId, currency, i18n.language);
      } else {
        const paypalRes: PaypalCreateResponse =
          await createPaypalOrder(reportId, currency, i18n.language);
        redirectTo = paypalRes.approve_url;
      }
      window.open(redirectTo, '_blank');
    } catch (err) {
      console.error('Error iniciando el pago', err);
      alert(t('purchaseModal.errorStartingPayment'));
    }
  };

  const filteredFindings = Object.entries(findings).filter(([,v]) => v > 0);
  const metricsDisplay: Record<string,string> = {
    num_trabajadores:         t('purchaseModal.metrics.workersFound'),
    num_filtraciones:         t('purchaseModal.metrics.exposedCredentials'),
    num_dominios_spoofeables: t('purchaseModal.metrics.spoofableDomains'),
    num_emails:               t('purchaseModal.metrics.corporateEmails'),
    num_ips:                  t('purchaseModal.metrics.ipsFound'),
    num_subdomains:           t('purchaseModal.metrics.subdomains'),
    num_typosquatting:        t('purchaseModal.metrics.typosquatting'),
    num_directories:          t('purchaseModal.metrics.foundDirectories'),
    num_technologies:         t('purchaseModal.metrics.detectedTechnologies'),
    len_vulns:                t('purchaseModal.metrics.vulnerabilities'),
    len_dir_sensibles:        t('purchaseModal.metrics.sensitiveDirectories'),
  };

  const benefits = [
    {
      icon: FileText,
      title: t('purchaseModal.benefits.intelligenceReportTitle'),
      description: t('purchaseModal.benefits.intelligenceReportDescription', {
        domains: domains.map(d=>d.domain_name).join(', ') || 'N/A',
        urls:    urls.map(u=>u.url).join(', ')          || 'N/A',
      }),
    },
    {
      icon: Shield,
      title: t('purchaseModal.benefits.continuousMonitoringTitle'),
      description: t('purchaseModal.benefits.continuousMonitoringDescription'),
    },
    {
      icon: MessageCircle,
      title: t('purchaseModal.benefits.supportTitle'),
      description: t('purchaseModal.benefits.supportDescription'),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-white rounded-lg">
        <div className="grid md:grid-cols-2">
          {/* IZQUIERDA: Hallazgos */}
          <div className="p-6 bg-gradient-to-br from-blue-50 to-white">
            <DialogHeader>
              <div className="flex items-center space-x-4 mb-6">
                <img
                  src={logo||unknownLogo}
                  alt={company}
                  className="w-16 h-16 rounded-full border-2 border-white shadow-lg"
                />
                <div>
                  <DialogTitle className="text-2xl">{company}</DialogTitle>
                  <DialogDescription>
                    {t('purchaseModal.dialogDescription')}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {filteredFindings.map(([k,v])=>(
                <div
                  key={k}
                  className="bg-white p-4 rounded-lg shadow-sm hover:shadow-lg hover:scale-105 transition-transform"
                >
                  <p className="text-3xl font-bold text-blue-600">{v}</p>
                  <p className="text-sm text-gray-600">
                    {metricsDisplay[k]||k.replace(/_/g,' ')}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* DERECHA: Precio, beneficios y pago */}
          <div className="p-6 bg-white border-l flex flex-col">
            {/* Precio */}
            <div className="text-center mb-8">
              {discountPercentage>0 && isDiscountValid && (
                <div className="flex justify-center items-center mb-2 space-x-4">
                  <span className="text-3xl line-through text-gray-500">
                    {price}{currencySymbol}
                  </span>
                  <span className="text-xl bg-red-100 text-red-700 px-3 py-1 rounded-full font-bold">
                    -{discountPercentage}%
                  </span>
                </div>
              )}
              <span className="text-5xl font-extrabold text-blue-700">
                {isDiscountValid ? finalPrice : price}{currencySymbol}
              </span>
              {isDiscountValid && remainingTime && (
                <p className="text-lg text-red-600 font-semibold mt-2">
                  {remainingTime}
                </p>
              )}
            </div>
            <Separator />

            {/* Beneficios */}
            <div className="space-y-4 mt-4 flex-1">
              <h3 className="font-semibold text-lg">{t('purchaseModal.includes')}</h3>
              {benefits.map((b,i)=>(
                <div key={i} className="flex items-start space-x-3">
                  <b.icon className="h-10 w-10 text-blue-600" />
                  <div>
                    <p className="font-medium">{b.title}</p>
                    <p className="text-sm text-gray-600">{b.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <Separator className="my-4"/>

            {/* Botón COMPRAR */}
            <div className="px-6 pb-2">
              <Button
                onClick={handlePurchase}
                className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
              >
                <Check className="mr-2 h-5 w-5" />
                {t('purchaseModal.buyNow')}
              </Button>
            </div>

            {/* Selector de pasarela abajo */}
            <div className="flex justify-center items-center space-x-6 mt-4 px-6">
              {/* Stripe */}
              <button
                onClick={() => setPaymentMethod('stripe')}
                className={`p-3 rounded-lg border transition ${
                  paymentMethod === 'stripe'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300'
                }`}
              >
                <CreditCard className="h-6 w-6 text-gray-700" />
              </button>

              {/* PayPal */}
              <button
                onClick={() => setPaymentMethod('paypal')}
                className={`p-3 rounded-lg border transition ${
                  paymentMethod === 'paypal'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300'
                }`}
              >
                <FaPaypal className="h-6 w-6 text-gray-700" />
              </button>
            </div>

            {/* Footer seguros */}
            <div className="flex justify-center items-center text-gray-500 space-x-2 pb-4 mt-4">
              <span className="text-xs">{t('purchaseModal.securePayment')}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseModal;
